<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';

  import { getNotificationsContext } from '@client/components/notifications';
  import { Step } from '@client/components/steps';

  import { BizCardRouteForm } from '@client/pages/bizcards/routing/modals/shared';

  import { BizCardsService } from '@client/services/bizcards';
  import { BizCardsRoutingService } from '@client/services/bizcards-routing';
  import { GroupsService } from '@client/services/groups';

  import { appStore } from '@client/stores/app';

  import { RoutingList } from '../index.js';

  /**
   * Controls whether the next button in the workflow step is disabled.
   * @type {boolean}
   * @default true
   */
  export let nextDisabled = true;

  /**
   * Callback function invoked when the workflow step executes.
   * @type {Function | null}
   * @default null
   */
  export let onExecute = null;

  /**
   * Callback function invoked when the workflow step is rolled back.
   * @type {Function | null}
   * @default null
   */
  export let onRollback = null;

  /**
   * Step configuration object containing step data and metadata.
   * @type {Object}
   * @default { data: {} }
   */
  export let step = { data: {} };

  const { addNotification } = getNotificationsContext();

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  let loading = true;
  let currentInternalStep = 0;
  let maxStepReached = 0;
  let selectedRouteId = null;
  let routes = [];
  let route = null;
  let originalRoute = null;
  let senderGroups = [];
  let groupOptions = [];
  let businessCardOptions = [];
  let isDisabled = false;

  const selectRoute = (routeId) => {
    selectedRouteId = routeId;
  };

  const handleInternalNext = (stepIndex) => {
    if (stepIndex === 0) {
      const selectedRoute = routes.find((r) => r._id === selectedRouteId);
      if (selectedRoute) {
        originalRoute = { ...selectedRoute };
        route = {
          ...selectedRoute,
          selectedGroups:
            selectedRoute.sendergroups?.map((g) => ({
              label: g.groupname,
              value: g.groupname
            })) || []
        };
        currentInternalStep = 1;
        if (currentInternalStep > maxStepReached) {
          maxStepReached = currentInternalStep;
        }
      }
    }
  };

  const handleInternalBack = (stepIndex) => {
    if (stepIndex > 0) {
      currentInternalStep = stepIndex - 1;
    }
  };

  const onSignatureChange = async (e) => {
    const blockname = e.detail.value;
    const response = await BizCardsService.getById(blockname);

    route = {
      ...route,
      bizcard: response,
      blockname
    };
  };

  const executeUpdate = async () => {
    $appStore.loading = true;

    try {
      const selectedGroups = (route.selectedGroups || []).map(({ value }) => value);
      const sendergroups = senderGroups.filter(({ groupname }) => selectedGroups.includes(groupname));
      const { blockname, bizcard = {}, _id } = route;

      const payload = {
        _id,
        blockname,
        bizcard,
        priority: route.priority - 1,
        sendergroups,
        status: route.status,
        tags: []
      };

      await BizCardsRoutingService.updateRoute(payload);

      notify($format('label.SUCCESS.UPDATE_ROUTE_FOR', { values: { blockname } }));

      step.data = {
        id: blockname,
        priority: route.priority,
        rollbackData: {
          originalRoute: originalRoute
        }
      };

      return { success: true };
    } catch (error) {
      notify($format('label.FAILURE.UPDATE_ROUTE'), 'error');
      throw error;
    } finally {
      $appStore.loading = false;
    }
  };

  const rollbackUpdate = async () => {
    $appStore.loading = true;

    try {
      const { originalRoute } = step.data.rollbackData;

      if (!originalRoute) {
        throw new Error($format('label.NO_ROLLBACK_DATA'));
      }

      const payload = {
        _id: originalRoute._id,
        blockname: originalRoute.blockname,
        bizcard: originalRoute.bizcard,
        priority: originalRoute.priority - 1,
        sendergroups: originalRoute.sendergroups || [],
        status: originalRoute.status,
        tags: []
      };

      await BizCardsRoutingService.updateRoute(payload);

      notify($format('label.SUCCESS.ROLLBACK_ROUTE'));

      return { success: true };
    } catch (error) {
      notify($format('label.FAILURE.ROLLBACK_ROUTE'), 'error');
      throw error;
    } finally {
      $appStore.loading = false;
    }
  };

  onMount(async () => {
    loading = true;

    try {
      const [groupsResponse, bizcardsResponse, routesResponse] = await Promise.all([
        GroupsService.getAll(),
        BizCardsService.getAll(),
        BizCardsRoutingService.getAll()
      ]);

      senderGroups = Array.isArray(groupsResponse) ? groupsResponse : [];
      groupOptions = senderGroups.map((group) => ({
        label: group.groupname,
        value: group.groupname
      }));

      const cards = Array.isArray(bizcardsResponse) ? bizcardsResponse : [];
      businessCardOptions = cards
        .filter(({ archived }) => !archived)
        .map(({ name, rulename }) => ({
          label: name || rulename,
          value: rulename
        }))
        .sort((a, b) => (a.label || '').localeCompare(b.label || ''));

      const bizcardRoutes = Array.isArray(routesResponse) ? routesResponse : [];

      routes = bizcardRoutes.map((route, index) => {
        const sendergroups = senderGroups.filter(
          ({ groupname }) => route.sendergroups.filter((group) => groupname === group.groupname).length
        );

        return {
          ...route,
          priority: index + 1,
          sendergroups
        };
      });
    } catch (error) {
      console.error('Failed to load bizcard routing data:', error);
      notify($format('label.FAILURE.LOAD_DATA'), 'error');
    } finally {
      loading = false;
    }

    if (onExecute) {
      onExecute(executeUpdate);
    }

    if (onRollback) {
      onRollback(rollbackUpdate);
    }
  });

  $: {
    if (currentInternalStep === 0) {
      isDisabled = !selectedRouteId || loading;
    } else if (currentInternalStep === 1) {
      isDisabled = !route?.blockname || !route?.selectedGroups?.length || loading;
    }
  }

  $: nextDisabled = currentInternalStep !== 1 || isDisabled;

  $: selectedRouteSummary = selectedRouteId
    ? routes.find((r) => r._id === selectedRouteId)?.blockname || $format('label.ROUTE_SELECTED')
    : $format('label.NOT_SELECTED');
</script>

<div class="bizcard-routing-update-form">
  <Step
    active={currentInternalStep === 0}
    disabled={false}
    title={$format('label.SELECT_ROUTE')}
    summary={selectedRouteSummary}
    onNext={currentInternalStep === 0 ? () => handleInternalNext(0) : undefined}
    onStep={() => (currentInternalStep = 0)}
    nextDisabled={isDisabled}
  >
    <RoutingList
      {routes}
      bind:selectedRouteId
      onSelectRoute={selectRoute}
      showTags={false}
      routeTypeLabel={$format('label.BUSINESS_CARD')}
      {loading}
    />
  </Step>

  <Step
    active={currentInternalStep === 1}
    disabled={maxStepReached < 1}
    title={$format('label.EDIT_ROUTE')}
    summary={route?.blockname || $format('label.NOT_CONFIGURED')}
    onBack={currentInternalStep === 1 ? () => handleInternalBack(1) : undefined}
    onStep={() => {
      if (currentInternalStep >= 1) currentInternalStep = 1;
    }}
    nextDisabled={isDisabled}
  >
    {#if route}
      <BizCardRouteForm bind:route bind:businessCardOptions bind:groupOptions {onSignatureChange} />
    {/if}
  </Step>
</div>

<style lang="scss">
  .bizcard-routing-update-form {
    :global {
      .route {
        margin-bottom: var(--margin-base);
      }
    }
  }
</style>
